import { useState } from 'react';
import { useCan, useSubjectLanguageVariants } from '@features/user';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Badge, Button } from '@mui/material';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import {
  useApproveChangeRequestMutation,
  useBookingRequestsQuery,
  useChangeRequestsQuery,
  useDeclineChangeRequestMutation,
} from '../store/api';

import { BookingRequestsPanel } from './BookingRequestsPanel';

/** Calendar-header entry point to the pending booking-requests queue.
 *
 * Renders nothing unless the self-scheduling feature gate is on AND the user
 * can manage the org calendar (the queue API is manager-gated). The badge
 * shows the pending count.
 */
export function BookingRequestsButton() {
  const schedulingFeatureEnabled = useFeatureGate(
    FeatureGates.PATIENT_SELF_SCHEDULING,
  );
  const canManageScheduling = useCan('manage_org_calendar');
  const [open, setOpen] = useState(false);
  const { bookingRequests } = useEventTracking();
  const { plural } = useSubjectLanguageVariants();

  const enabled = schedulingFeatureEnabled && canManageScheduling;
  const { data, isLoading } = useBookingRequestsQuery(
    {},
    { skip: !enabled, pollingInterval: 60_000 },
  );
  const { data: changeRequestData } = useChangeRequestsQuery(undefined, {
    skip: !enabled,
    pollingInterval: 60_000,
  });
  const [approveChangeRequest, { originalArgs: approvingArgs, isLoading: approving }] =
    useApproveChangeRequestMutation();
  const [declineChangeRequest, { originalArgs: decliningArgs, isLoading: declining }] =
    useDeclineChangeRequestMutation();
  const [changeRequestError, setChangeRequestError] = useState<string | null>(
    null,
  );

  if (!enabled) {
    return null;
  }

  const changeRequests = (changeRequestData?.items ?? []).map(request => ({
    requestId: request.appointment_change_request_id,
    requesterName: request.requester_name,
    currentStartTime: request.current_start_time,
    requestedStartTime: request.requested_start_time,
  }));
  const decidingRequestId =
    (approving ? approvingArgs?.requestId : null) ??
    (declining ? decliningArgs?.requestId : null) ??
    null;

  const decisionError = (error: unknown): string => {
    const maybeQueryError = error as FetchBaseQueryError | SerializedError;
    if (
      'data' in maybeQueryError &&
      maybeQueryError.data &&
      typeof maybeQueryError.data === 'object' &&
      (maybeQueryError.data as { error_code?: string }).error_code ===
        'slot_unavailable'
    ) {
      return 'That requested time was just taken — decline the request or ask the patient to pick again.';
    }
    return "We couldn't apply that decision. Please try again.";
  };

  const handleApprove = (requestId: string) => {
    setChangeRequestError(null);
    approveChangeRequest({ requestId })
      .unwrap()
      .catch((error: unknown) => setChangeRequestError(decisionError(error)));
  };
  const handleDecline = (requestId: string) => {
    setChangeRequestError(null);
    declineChangeRequest({ requestId })
      .unwrap()
      .catch((error: unknown) => setChangeRequestError(decisionError(error)));
  };

  const items = (data?.items ?? []).map(item => ({
    bookingProspectId: item.booking_prospect_id,
    appointmentId: item.appointment_id,
    appointmentStatus: item.appointment_status,
    appointmentStartTime: item.appointment_start_time,
    firstName: item.first_name,
    lastName: item.last_name,
    email: item.email,
    phone: item.phone,
    reason: item.reason,
    gateResults: item.gate_results,
    hasCardOnFile: item.has_card_on_file,
    rescheduledByPatient: item.rescheduled_by_patient,
  }));
  // Confirmed bookings stay listed (identity review is a later step) but are
  // no longer waiting on the user — only unconfirmed ones light the badge.
  const pendingCount =
    items.filter(item => item.appointmentStatus !== 'scheduled').length +
    changeRequests.length;

  const handleOpen = () => {
    bookingRequests.opened({ pending_count: pendingCount });
    setOpen(true);
  };

  return (
    <>
      <Badge badgeContent={pendingCount} color="warning" overlap="rectangular">
        <Button
          variant="outlined"
          size="small"
          startIcon={<InboxOutlinedIcon />}
          onClick={handleOpen}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Requests
        </Button>
      </Badge>
      <BookingRequestsPanel
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        isLoading={isLoading}
        subjectPlural={plural}
        changeRequests={changeRequests}
        onApproveChangeRequest={handleApprove}
        onDeclineChangeRequest={handleDecline}
        decidingRequestId={decidingRequestId}
        changeRequestError={changeRequestError}
      />
    </>
  );
}
